import { type ReactNode, useEffect, useRef, useState } from 'react'

interface ProgressCircleProps {
  rate: number
  color: ProgressCircleColor
  fullColor: ProgressCircleColor
  unavailable?: boolean
  children?: ReactNode
}

interface ProgressCircleGradient {
  start: string
  end: string
}

type ProgressCircleColor = 'blue' | 'green'

interface ProgressCircleBadgeColors {
  border: string
  background: string
  text: string
}

const RENDERED_SIZE = 130
const THICKNESS = 12
const TRACK_BORDER_THICKNESS = THICKNESS + 2
const PROGRESS_THICKNESS = TRACK_BORDER_THICKNESS
const ANIMATION_DURATION_PER_POINT_MS = 6
const MIN_ANIMATION_DURATION_MS = 1
const MAX_ANIMATION_DURATION_MS = 600

const FULL_GAUGE_RATE = 100
const MAX_RENDERED_RATE = 200
const DEGREES_PER_RATE_POINT = 3.6
const MAX_SEGMENT_DEGREES = 2
const GAUGE_COLORS: Record<ProgressCircleColor, ProgressCircleGradient> = {
  blue: {
    start: '#1A30FF',
    end: '#006BE6'
  },
  green: {
    start: '#00B95F',
    end: '#00EB72'
  }
}
const BADGE_COLORS: Record<ProgressCircleColor, ProgressCircleBadgeColors> = {
  blue: {
    border: 'rgba(54, 67, 186, 0.8)',
    background: 'var(--vp-semantic-color-status-info-quiet)',
    text: 'var(--vp-semantic-color-extended-cobalt)'
  },
  green: {
    border: 'var(--vp-semantic-color-extended-green)',
    background: 'var(--vp-semantic-color-extended-green-quiet)',
    text: 'var(--vp-semantic-color-extended-green)'
  }
}

const getAnimationDuration = (rate: number) =>
  Math.min(Math.max(rate * ANIMATION_DURATION_PER_POINT_MS, MIN_ANIMATION_DURATION_MS), MAX_ANIMATION_DURATION_MS)

const easeOutCubic = (progress: number) => 1 - Math.pow(1 - progress, 3)

const useAnimatedRate = (targetRate: number) => {
  const [animatedRate, setAnimatedRate] = useState(0)
  const animatedRateRef = useRef(0)

  useEffect(() => {
    animatedRateRef.current = animatedRate
  }, [animatedRate])

  useEffect(() => {
    const startRate = animatedRateRef.current

    if (targetRate === startRate) {
      return
    }

    if (targetRate === 0) {
      setAnimatedRate(0)
      return
    }

    const durationMs = getAnimationDuration(Math.abs(targetRate - startRate))
    const startTime = performance.now()
    let animationFrameId: number

    const animate = (now: number) => {
      const progress = Math.min((now - startTime) / durationMs, 1)

      if (progress < 1) {
        setAnimatedRate(Math.round(startRate + (targetRate - startRate) * easeOutCubic(progress)))
        animationFrameId = requestAnimationFrame(animate)
        return
      }

      setAnimatedRate(targetRate)
    }

    animationFrameId = requestAnimationFrame(animate)

    return () => {
      cancelAnimationFrame(animationFrameId)
    }
  }, [targetRate])

  return animatedRate
}

const interpolateHexColor = (startHex: string, endHex: string, ratio: number) => {
  const clampedRatio = Math.min(Math.max(ratio, 0), 1)
  const start = startHex.match(/\w\w/g)?.map(value => Number.parseInt(value, 16)) ?? [0, 0, 0]
  const end = endHex.match(/\w\w/g)?.map(value => Number.parseInt(value, 16)) ?? [0, 0, 0]
  const [red, green, blue] = start.map((startValue, index) =>
    Math.round(startValue + (end[index] - startValue) * clampedRatio)
  )

  return `#${[red, green, blue].map(value => value.toString(16).padStart(2, '0')).join('')}`
}

const getPointOnCircle = (center: number, radius: number, angleInDegrees: number) => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180

  return {
    x: center + radius * Math.cos(angleInRadians),
    y: center + radius * Math.sin(angleInRadians)
  }
}

const getArcSegmentPath = (center: number, radius: number, startAngle: number, endAngle: number) => {
  const start = getPointOnCircle(center, radius, startAngle)
  const end = getPointOnCircle(center, radius, endAngle)

  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${endAngle - startAngle > 180 ? 1 : 0} 1 ${end.x} ${end.y}`
}

const getProgressSegments = (totalDegrees: number) => {
  if (totalDegrees <= 0) {
    return []
  }

  const segmentCount = Math.ceil(totalDegrees / MAX_SEGMENT_DEGREES)

  return Array.from({ length: segmentCount }, (_, index) => {
    const startAngle = (totalDegrees / segmentCount) * index
    const endAngle = (totalDegrees / segmentCount) * (index + 1)

    return { startAngle, endAngle }
  })
}

const ProgressCircle = ({ rate, color, fullColor, unavailable = false, children }: Readonly<ProgressCircleProps>) => {
  const targetRate = unavailable ? 0 : Math.max(rate, 0)
  const renderedRate = Math.min(targetRate, MAX_RENDERED_RATE)
  const animatedRenderedRate = useAnimatedRate(renderedRate)
  const displayRate = animatedRenderedRate === renderedRate ? targetRate : animatedRenderedRate
  const isFullGauge = targetRate >= FULL_GAUGE_RATE
  const center = RENDERED_SIZE / 2
  const radius = center - PROGRESS_THICKNESS / 2
  const totalProgressDegrees = animatedRenderedRate * DEGREES_PER_RATE_POINT
  const startPoint = getPointOnCircle(center, radius, 0)
  const capPoint = getPointOnCircle(center, radius, totalProgressDegrees % 360)
  const progressColor = isFullGauge ? fullColor : color
  const gaugeColors = GAUGE_COLORS[progressColor]
  const badgeColors = BADGE_COLORS[progressColor]
  const hasVisibleProgress = displayRate > 0
  const progressSegments = getProgressSegments(totalProgressDegrees)

  return (
    <div
      data-testid="progress-circle"
      data-rate={targetRate}
      className="mg-progress-circle"
      style={{
        width: RENDERED_SIZE,
        height: RENDERED_SIZE
      }}
    >
      <svg aria-hidden className="mg-progress-circle-svg" viewBox={`0 0 ${RENDERED_SIZE} ${RENDERED_SIZE}`}>
        <circle
          className="mg-progress-circle-track-border"
          cx={center}
          cy={center}
          data-testid="progress-circle-track-border"
          fill="none"
          r={radius}
          strokeWidth={TRACK_BORDER_THICKNESS}
        />
        <circle
          className="mg-progress-circle-track"
          cx={center}
          cy={center}
          data-testid="progress-circle-track"
          fill="none"
          r={radius}
          strokeWidth={THICKNESS}
        />
        {hasVisibleProgress && (
          <circle
            cx={startPoint.x}
            cy={startPoint.y}
            data-testid="progress-circle-indicator-start-cap"
            fill={gaugeColors.start}
            r={PROGRESS_THICKNESS / 2}
          />
        )}
        {progressSegments.map((segment, index) => {
          const isFirstSegment = index === 0
          const isLastSegment = index === progressSegments.length - 1
          const color = interpolateHexColor(
            gaugeColors.start,
            gaugeColors.end,
            totalProgressDegrees === 0 ? 0 : segment.endAngle / totalProgressDegrees
          )

          return (
            <path
              key={`${segment.startAngle}-${segment.endAngle}`}
              d={getArcSegmentPath(center, radius, segment.startAngle, segment.endAngle)}
              data-testid={
                isFirstSegment
                  ? 'progress-circle-indicator'
                  : isLastSegment
                    ? 'progress-circle-indicator-end'
                    : undefined
              }
              fill="none"
              stroke={color}
              strokeLinecap={isLastSegment ? 'round' : 'butt'}
              strokeLinejoin="round"
              strokeWidth={PROGRESS_THICKNESS}
            />
          )
        })}
        {isFullGauge && (
          <circle
            cx={capPoint.x}
            cy={capPoint.y}
            data-testid="progress-circle-indicator-cap"
            fill={gaugeColors.end}
            r={PROGRESS_THICKNESS / 2}
          />
        )}
      </svg>

      <div className="mg-progress-circle-content" style={{ inset: THICKNESS }}>
        {children}
      </div>

      <p
        className="mg-progress-circle-badge vp-body-s"
        data-testid="progress-circle-rate"
        style={{
          borderColor: badgeColors.border,
          background: badgeColors.background,
          color: badgeColors.text
        }}
      >
        {unavailable ? '--%' : `${displayRate}%`}
      </p>
    </div>
  )
}

export default ProgressCircle
